import { Box, Stack, Typography } from "@mui/material";
import React from "react";

/**
 * Una riga dei tempi SLA del chip compatto. Le righe sono costruite **una sola
 * volta** da `SlaTaskSlaBadge` (stessa formattazione) e rese qui in due forme:
 * il tooltip su desktop e il bottom sheet su touch.
 */
export interface SlaTaskSlaTimeRow {
  key: string;
  /** Etichetta della grandezza; assente per le righe solo-testo (pausa, override). */
  label?: string;
  value: React.ReactNode;
  /** Riga in evidenza (riepilogo attivita' / processo peggiore). */
  strong?: boolean;
  /** Riga di riepilogo del processo: staccata dalle righe dell'attivita'. */
  separated?: boolean;
  /**
   * Stato di salute: nel tooltip e' una riga, nel sheet e' gia' il chip di
   * testata e quindi non si ripete.
   */
  health?: boolean;
}

interface SlaTaskSlaTimesProps {
  rows: SlaTaskSlaTimeRow[];
  layout: "tooltip" | "sheet";
}

const SlaTaskSlaTimes: React.FC<SlaTaskSlaTimesProps> = ({ rows, layout }) => {
  if (layout === "tooltip") {
    return (
      <Stack spacing={0.25}>
        {rows.map((row) => {
          const text = (
            <>
              {row.label ? `${row.label}: ` : ""}
              {row.value}
            </>
          );
          if (row.separated) {
            return (
              <Box
                key={row.key}
                component="span"
                sx={{ pt: 0.5, fontWeight: row.strong ? 700 : 400 }}
              >
                {text}
              </Box>
            );
          }
          return row.strong ? <strong key={row.key}>{text}</strong> : <span key={row.key}>{text}</span>;
        })}
      </Stack>
    );
  }

  return (
    <Stack>
      {rows
        .filter((row) => !row.health)
        .map((row, index) => (
          <Box
            key={row.key}
            sx={{
              display: "flex",
              alignItems: "baseline",
              justifyContent: "space-between",
              gap: 2,
              py: 1.25,
              borderTop: index > 0 ? "1px solid" : "none",
              borderColor: "divider",
              mt: row.separated ? 0.5 : 0,
            }}
          >
            {row.label ? (
              <>
                <Typography variant="body2" color="text.secondary">
                  {row.label}
                </Typography>
                <Typography
                  component="div"
                  variant="body2"
                  sx={{ textAlign: "right", fontWeight: row.strong ? 700 : 500 }}
                >
                  {row.value}
                </Typography>
              </>
            ) : (
              <Typography
                component="div"
                variant="body2"
                sx={{ fontWeight: row.strong ? 700 : 400 }}
              >
                {row.value}
              </Typography>
            )}
          </Box>
        ))}
    </Stack>
  );
};

export default SlaTaskSlaTimes;
